import { component$, Slot, useStyles$ } from "@builder.io/qwik";
import { Modal } from "@qwik-ui/headless";
import { CloseIcon } from "../../icons/CloseIcon";
import { cn } from "../../utils/tailwind-helper";
import styles from "./modal.css?inline";
import { BREAKPOINTS } from "~/constants/mediaquery";

export enum ModelTypes {
    Form = "form",
    Detail = "detail",
}

export interface StyledModelProps {
    modelType: ModelTypes;
    customWidth?: {
        bronze?: string;
        silver?: string;
        gold?: string;
        platinum?: string;
    };
}

export const StyledModel = component$((props: StyledModelProps) => {
    useStyles$(styles);
    const { modelType, customWidth } = props;

    return (
        <Modal.Root
            onShow$={() => {
                if (window.innerWidth >= BREAKPOINTS.SILVER && "ontouchstart" in window === false) {
                    document.body.classList.add("mr-[15px]", "!pr-0", "border-r");
                }
            }}
        >
            <Modal.Trigger class="modal-trigger">
                <Slot name="trigger" />
            </Modal.Trigger>
            <Modal.Panel
                class="modal-panel sheet w-full"
                onTransitionStart$={(e) => {
                    const { animationDuration } = getComputedStyle(e.target as HTMLElement);

                    let startTime: number;
                    let animationFrameId: number;

                    const animate = (timestamp: number) => {
                        if (!startTime) startTime = timestamp;
                        const elapsed = timestamp - startTime;

                        if (elapsed >= parseFloat(animationDuration) * 1000 * 0.25) {
                            document?.body.classList.remove("mr-[15px]", "border-r");
                            document?.body.classList.add("!overflow-auto");
                        }

                        if (elapsed >= parseFloat(animationDuration) * 1000) {
                            document?.body.classList.remove("!overflow-auto", "!pr-0");
                            cancelAnimationFrame(animationFrameId);
                            return;
                        }
                        animationFrameId = requestAnimationFrame(animate);
                    };

                    animationFrameId = requestAnimationFrame(animate);
                }}
            >
                <div
                    class={cn(
                        "relative h-full w-full", // adjust as needed
                        "z-20",
                    )}
                >
                    <div
                        class={cn(
                            "relative h-full w-full overflow-y-auto overflow-x-hidden bg-white",
                            "ml-auto",
                            "silver:mt-0 silver:h-full",
                            // If customWidth is provided, use it regardless of modelType
                            customWidth?.silver ||
                                (modelType === ModelTypes.Form ? "silver:w-[720px]" : "silver:w-[802px]"),
                            customWidth?.gold || (modelType === ModelTypes.Form ? "gold:w-[720px]" : "gold:w-[1036px]"),
                            customWidth?.platinum ||
                                (modelType === ModelTypes.Form ? "platinum:w-[720px]" : "platinum:w-[1053px]"),
                        )}
                    >
                        {/* Close Button (top-right) */}
                        <Modal.Close
                            class={cn(
                                "modal-close",
                                "absolute right-4 top-4 z-40", // ensuring on top of image
                                "bronze:h-8 bronze:w-8",
                                "silver:left-4 silver:right-[inherit] silver:h-8 silver:w-8",
                                "gold:h-8 gold:w-8",
                                "platinum:h-8 platinum:w-8",
                                "flex items-center justify-center rounded-full bg-primary-500/5",
                            )}
                        >
                            <CloseIcon class="h-4 w-4 text-primary-500" />
                        </Modal.Close>

                        {/* The actual form container */}
                        <div
                            class={cn(
                                modelType === ModelTypes.Form && "bronze:p-4", // margin 16 in the design? We'll do p-4 for bronze
                                modelType === ModelTypes.Form && "silver:p-8", // 32px at silver
                                modelType === ModelTypes.Form && "gold:p-20", // 40px at gold
                                modelType === ModelTypes.Detail && "h-full w-full",
                            )}
                        >
                            <div
                                class={cn(
                                    modelType === ModelTypes.Detail && "h-full w-full",
                                    "relative z-30", // so form is above the background
                                    modelType === ModelTypes.Form && "overflow-y-auto overflow-x-hidden",
                                )}
                            >
                                <Slot name="content" />
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Panel>
        </Modal.Root>
    );
});
